import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { faUsers } from "@fortawesome/free-solid-svg-icons";
import {
    Content,
    Card,
    CardHeader,
    CardBody,
    Row,
    Col,
    FloatInput,
    ContentLoader,
    Button,
    LoadingSpinner,
    Switch,
    Alert,
} from "@webtypen/react-components";
import { Page } from "../../components/Page";
import { ApiHelper, ConfirmHelper } from "../../helpers";

const defaultState = {
    firstname: "",
    lastname: "",
    email: "",
    number: "",
    is_active: true,
    generate_number: true,
    set_new_password: false,
    can_book_workingshours: false,
    new_password: "",
    new_password_repeat: "",
    vacation_days_total: 4,
    vacation_days_available: 4,
};

export const EmployeesEdit = () => {
    const params = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [data, setData] = useState(null);

    const onChange = (e) => {
        setData({
            ...data,
            [e.target.name]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
        });
    };

    const submit = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (
            (data.set_new_password && !data.new_password) ||
            (data.set_new_password && data.new_password.trim().length < 5)
        ) {
            ConfirmHelper.alert("Das neue Passwort muss mindestens 5 Zeichen lang sein ...");
            return;
        }

        if (data.set_new_password && data.new_password !== data.new_password_repeat) {
            ConfirmHelper.alert("Die beiden Passwörter stimmen nicht überein ...");
            return;
        }

        setIsSaving(true);
        ApiHelper.request("/api/employees/save", {
            ...data,
            _id: params.id,
        })
            .then((response) => {
                if (response.status === "success" && response.data && response.data._id) {
                    navigate("/employees");
                } else {
                    ConfirmHelper.alert(
                        response.status === "error" && response.message && response.message.trim() !== ""
                            ? response.message
                            : "Es ist ein unerwarteter Fehler aufgetreten ..."
                    );
                    setIsSaving(false);
                }
            })
            .catch((e) => {
                console.error(e);

                ConfirmHelper.alert(
                    e && e.data && e.data.status === "error" && e.data.message && e.data.message.trim() !== ""
                        ? e.data.message
                        : "Es ist ein unerwarteter Fehler aufgetreten ..."
                );
                setIsSaving(false);
            });
    };

    useEffect(() => {
        if (params.id === "new") {
            setData({ ...defaultState, set_new_password: true });
            setIsLoading(false);
        } else {
            setIsLoading(true);
            ApiHelper.request("/api/employees/data", {
                _id: params.id,
            })
                .then((response) => {
                    setData(response.status === "success" && response.data && response.data._id ? response.data : null);
                    setIsLoading(false);
                })
                .catch((e) => {
                    console.error(e);
                    setData(null);
                    setIsLoading(false);
                });
        }
    }, [params.id]);

    if (isLoading || !data) {
        return (
            <Page icon={faUsers} title="Mitarbeiter" back="/employees">
                <Content>
                    {isLoading ? <ContentLoader /> : <Alert danger>Fehler beim Laden des Datensatzes ...</Alert>}
                </Content>
            </Page>
        );
    }

    return (
        <Page icon={faUsers} title="Mitarbeiter" back="/employees">
            <Content>
                <form onSubmit={submit}>
                    <Row>
                        <Col width={12}>
                            <Card className="mb-3">
                                <CardHeader>Hauptinformationen</CardHeader>
                                <CardBody>
                                    <Row>
                                        <Col width={3} className="mb-2">
                                            <FloatInput
                                                type="text"
                                                name="lastname"
                                                placeholder="Nachname"
                                                value={data.lastname ? data.lastname : ""}
                                                onChange={onChange}
                                                disabled={isSaving}
                                            />
                                        </Col>
                                        <Col width={3} className="mb-2">
                                            <FloatInput
                                                type="text"
                                                name="firstname"
                                                placeholder="Vorname"
                                                value={data.firstname ? data.firstname : ""}
                                                onChange={onChange}
                                                disabled={isSaving}
                                            />
                                        </Col>
                                        <Col width={4} className="mb-2">
                                            <FloatInput
                                                type="email"
                                                name="email"
                                                placeholder="E-Mail"
                                                value={data.email ? data.email : ""}
                                                onChange={onChange}
                                                disabled={isSaving}
                                            />
                                        </Col>

                                        <Col width={2} className="mb-2">
                                            <FloatInput
                                                type="text"
                                                name="number"
                                                placeholder="Mitarbeiter-Nr"
                                                value={data.number && !data.generate_number ? data.number : ""}
                                                onChange={onChange}
                                                disabled={isSaving || data.generate_number}
                                            />
                                        </Col>
                                        <Col
                                            width={12}
                                            style={{ display: "flex", alignItems: "center" }}
                                            className="mt-2"
                                        >
                                            <Switch
                                                name="is_active"
                                                checked={data.is_active ? true : false}
                                                onChange={onChange}
                                                disabled={isSaving}
                                                className="mr-2"
                                            />
                                            Mitarbeiter ist aktiviert / darf sich einloggen
                                        </Col>
                                        <Col
                                            width={12}
                                            style={{ display: "flex", alignItems: "center" }}
                                            className="mt-2"
                                        >
                                            <Switch
                                                name="can_book_workingshours"
                                                checked={data.can_book_workingshours ? true : false}
                                                onChange={onChange}
                                                disabled={isSaving}
                                                className="mr-2"
                                            />
                                            Mitarbeiter darf Arbeitsstunden buchen
                                        </Col>
                                        {!data.number || data.number.toString().trim() === "" ? (
                                            <Col
                                                width={12}
                                                style={{ display: "flex", alignItems: "center" }}
                                                className="mt-2"
                                            >
                                                <Switch
                                                    name="generate_number"
                                                    checked={data.generate_number ? true : false}
                                                    onChange={onChange}
                                                    disabled={isSaving}
                                                    className="mr-2"
                                                />
                                                Neue Mitarbeiternummer generieren
                                            </Col>
                                        ) : null}

                                        <Col
                                            width={12}
                                            style={{ display: "flex", alignItems: "center" }}
                                            className="mt-2 mb-3"
                                        >
                                            <Switch
                                                name="set_new_password"
                                                checked={data.set_new_password ? true : false}
                                                onChange={onChange}
                                                disabled={isSaving || params.id === "new"}
                                                className="mr-2"
                                            />
                                            Neues Passwort setzen
                                        </Col>

                                        {data.set_new_password ? (
                                            <Col width={12}>
                                                <Row>
                                                    <Col width={4} className="mb-2">
                                                        <FloatInput
                                                            type="password"
                                                            name="new_password"
                                                            placeholder="Neues Passwort"
                                                            value={data.new_password ? data.new_password : ""}
                                                            onChange={onChange}
                                                            disabled={isSaving}
                                                        />
                                                    </Col>
                                                    <Col width={4} className="mb-2">
                                                        <FloatInput
                                                            type="password"
                                                            name="new_password_repeat"
                                                            placeholder="Neues Passwort (wiederholen)"
                                                            value={
                                                                data.new_password_repeat ? data.new_password_repeat : ""
                                                            }
                                                            onChange={onChange}
                                                            disabled={isSaving}
                                                        />
                                                    </Col>
                                                </Row>
                                            </Col>
                                        ) : null}
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col width={4}>
                            <Card className="mb-3">
                                <CardHeader>Urlaub</CardHeader>
                                <CardBody>
                                    <FloatInput
                                        type="number"
                                        name="vacation_days_total"
                                        placeholder="Urlaubstage gesamt"
                                        value={data.vacation_days_total ? data.vacation_days_total : ""}
                                        onChange={onChange}
                                        disabled={isSaving}
                                        className="mb-2"
                                    />
                                    <FloatInput
                                        type="number"
                                        name="vacation_days_available"
                                        placeholder="Urlaubstage verfügbar"
                                        value={data.vacation_days_available ? data.vacation_days_available : ""}
                                        onChange={onChange}
                                        disabled={isSaving}
                                    />
                                </CardBody>
                            </Card>
                        </Col>

                        <Col width={12} className="text-right">
                            <Button disabled={isSaving}>{isSaving ? <LoadingSpinner /> : "Speichern"}</Button>
                        </Col>
                    </Row>
                </form>
            </Content>
        </Page>
    );
};
